import React, {useEffect, useState} from 'react';
import styled from 'styled-components/native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {getHomeTabItemByKey} from '../../../../utils/helpers';
import {Space} from '../../home';
import useResponsiveDimensions from '../../../../utils/hooks/useResponsiveDimensions';
import Animated, {FadeInDown} from 'react-native-reanimated';
import {tr} from '../../../../translations';
import SelectDrugDisease from '../../../../components/selecteDrugDisease';
import {TabKey} from '../../../../utils/enums';
import {getNews} from '../../tabNews/actions';
import {DrugItemType} from '../../../../interfaces/api/tabs';
import screens from '../../../../config/screens';
import {NavigationProp, useNavigation} from '@react-navigation/native';
import {useCustomHeaderHeight} from '../../../../utils/hooks/useHeaderHeight';
import {DrugsDiseasesSearchProps} from '../../../../interfaces/screens/drugsDiseasesSearch';

const Container = styled.View<{headerHeight: number}>`
  width: 100%;
  height: 100%;
  padding-top: ${({headerHeight}) => headerHeight}px;
`;

/**
 * DrugsDiseasesSearch screen component.
 *
 * @param {DrugsDiseasesSearchProps} props - The screen component props.
 */
const DrugsDiseasesSearch: React.FC<DrugsDiseasesSearchProps> = ({
  tabKey,
  search,
  topComponent,
}: DrugsDiseasesSearchProps) => {
  const navigation = useNavigation<NavigationProp<any>>();
  const {bottom: bottomInset} = useSafeAreaInsets();
  const headerHeight = useCustomHeaderHeight();
  const {hdp, getByScreenSize} = useResponsiveDimensions();
  const pageSize = getByScreenSize(30, 40);
  const [data, setData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [page, setPage] = useState(1);
  const [isLastPage, setIsLastPage] = useState<boolean>(false);

  const loadMore = async (refresh = false) => {
    if (isLoading && !refresh) {
      return;
    }
    setIsError(false);
    if (!isLastPage || refresh) {
      if (refresh) {
        setData([]);
      }
      setIsLoading(true);
      const result = await getNews({
        tab_id: getHomeTabItemByKey(tabKey)?.id,
        page: refresh ? 1 : page,
        ...(search ? {keywords: search} : {}),
        pageSize,
      });

      if (!result) {
        setIsError(true);
        setIsLoading(false);
        setIsLastPage(true);
        return;
      }
      setData(prev => {
        if (refresh) {
          return result?.data || [];
        } else {
          return [...prev, ...(result?.data || [])].filter(
            (value, index, self) => {
              return (
                self.findIndex(obj =>
                  ['id'].every(key => obj[key] === value[key]),
                ) === index
              );
            },
          );
        }
      });

      setPage(prev => (refresh ? 2 : prev + 1));
      const totalPageCount = Math.ceil(
        parseFloat(result?.total + '' || '2') / pageSize,
      );
      setIsLastPage((refresh ? 1 : page) >= totalPageCount);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadMore(true);
  }, [search]);

  const onDrugDiseaseSelectedPressHandler = (item: DrugItemType) => {
    navigation?.navigate(screens.content.drugsDiseasesDetails, {
      tabKey,
      id: item?.id,
      title: item?.name,
    });
  };

  return (
    <Container headerHeight={headerHeight}>
      {topComponent?.()}
      <Animated.View style={{flex: 1}} entering={FadeInDown.duration(400)}>
        <SelectDrugDisease
          data={data || []}
          title={tr(
            `drugsAndDiseases.${tabKey === TabKey.Diseases ? 'selectDiseases' : 'selectDrug'}`,
          )}
          onRefresh={() => loadMore(true)}
          onEndReached={() => loadMore()}
          isLoading={isLoading}
          isError={isError}
          onDrugDiseaseSelected={onDrugDiseaseSelectedPressHandler}
          height={hdp(50) - bottomInset - headerHeight}
        />
      </Animated.View>
      <Space bottomInset={bottomInset} extraSpace={hdp(11)} />
    </Container>
  );
};

export default DrugsDiseasesSearch;
